import React, { useRef } from "react"

import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import { Badge, type BadgeProps } from "@coval/ui"

import type { SubvalueByTimestamp } from "@/types/RunMetricOutput"
import { cn } from "@/utils/cn"

const fmt = (sec: number) =>
  `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}`

function getSeverityBadge(severity: number): {
  variant: BadgeProps["variant"]
  className?: string
} {
  if (severity >= 0.7) {
    return { variant: "danger" }
  }
  if (severity >= 0.5) {
    return { variant: "warning" }
  }
  if (severity >= 0.3) {
    return {
      variant: "default",
      className:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    }
  }
  return {
    variant: "success",
    className: "dark:bg-green-900/30 dark:text-green-300",
  }
}

interface MetadataField {
  key: string
  label: string
  format?: (value: number) => string
  colorFn?: (value: number) => string
}

interface AnomalyEventListProps {
  subvalues: SubvalueByTimestamp[]
  severityThreshold?: number

  chipLabel?: string
  title?: string
  groupByLabel?: boolean
  onFilterChange?: (filtered: SubvalueByTimestamp[] | null) => void
  metadataFields?: MetadataField[]
  /**
   * Optional: returns the rgba color string used for the audio-waveform
   * region when this subvalue is hovered/scoped. Lets consumers color-code
   * different anomaly types (e.g. TTS loops vs repeated sounds).
   */
  regionColorFn?: (sv: SubvalueByTimestamp) => string
  /**
   * Optional: returns Tailwind classes for the colored chip rendered in
   * each group header (groupByLabel mode). Receives the first subvalue of
   * the group as a representative.
   */
  chipColorClassFn?: (sv: SubvalueByTimestamp) => string
}

function AnomalyEventList({
  subvalues,
  severityThreshold = 0,

  chipLabel,
  title = "Events",
  groupByLabel = false,
  onFilterChange,
  metadataFields,
  regionColorFn,
  chipColorClassFn,
}: AnomalyEventListProps) {
  const openLabelsRef = useRef(new Set<string>())
  const belowThresholdOpenRef = useRef(false)
  const lastAboveRef = useRef<SubvalueByTimestamp[] | null>(null)

  const filtered = subvalues.filter(
    (sv) => (sv.float_value ?? 0) >= severityThreshold,
  )
  const hiddenEvents = subvalues.filter(
    (sv) => (sv.float_value ?? 0) < severityThreshold,
  )
  const hiddenCount = hiddenEvents.length

  if (subvalues.length === 0) {
    return null
  }

  const handleSeek = (e: React.MouseEvent, timestamp: number) => {
    e.stopPropagation()
    simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, { time: timestamp })
  }

  const renderRow = (sv: SubvalueByTimestamp, index: number) => {
    const severity = sv.float_value ?? 0
    const meta = sv.metadata ?? {}

    const borderColor =
      severity >= 0.7
        ? "border-l-red-500 dark:border-l-red-400"
        : severity >= 0.5
          ? "border-l-orange-400 dark:border-l-orange-300"
          : severity >= 0.3
            ? "border-l-yellow-400 dark:border-l-yellow-300"
            : "border-l-green-400 dark:border-l-green-300"

    const severityBadge = getSeverityBadge(severity)

    const hasMetadata = metadataFields?.some(
      (f) => meta[f.key] != null && typeof meta[f.key] === "number",
    )

    return (
      <div
        key={index}
        className={`px-3 py-1.5 border border-l-3 rounded-lg transition-colors cursor-pointer border-coval-border bg-surface-1 hover:bg-surface-1-hover ${borderColor}`}
        onClick={(e) => handleSeek(e, sv.start_offset)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-coval-text-muted font-mono">
            {fmt(sv.start_offset)} – {fmt(sv.end_offset)}
          </span>
          <span className="text-xs text-coval-text-muted">
            ({(sv.end_offset - sv.start_offset).toFixed(1)}s)
          </span>
          {severity > 0 && (
            <Badge
              variant={severityBadge.variant}
              size="status"
              className={cn("ml-auto font-mono", severityBadge.className)}
            >
              {severity.toFixed(2)}
            </Badge>
          )}
        </div>
        {hasMetadata && metadataFields && (
          <div className="flex items-center gap-3 mt-0.5">
            {metadataFields.map((field) => {
              const value = meta[field.key]
              if (value == null || typeof value !== "number") {
                return null
              }
              const formatted = field.format
                ? field.format(value)
                : value.toFixed(2)
              const color = field.colorFn
                ? field.colorFn(value)
                : "text-coval-text-muted"
              return (
                <span key={field.key} className={`text-xs font-mono ${color}`}>
                  {field.label}: {formatted}
                </span>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const handleBelowThresholdToggle = (isOpen: boolean) => {
    belowThresholdOpenRef.current = isOpen
    if (!onFilterChange) {
      if (isOpen) {
        const regions = hiddenEvents.map((sv, idx) => ({
          id: `below_threshold:${idx}`,
          start: sv.start_offset,
          end: Math.max(sv.end_offset, sv.start_offset + 0.5),
          color: "rgba(100, 116, 139, 0.2)",
          drag: false,
          resize: false,
        }))
        simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_REGIONS_HOVER, {
          regions,
        })
      } else {
        simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_REGIONS_HOVER, {
          regions: null,
        })
      }
      return
    }
    const belowEvents = isOpen ? hiddenEvents : []
    const combined = [...(lastAboveRef.current ?? []), ...belowEvents]
    onFilterChange(combined.length > 0 ? combined : null)
  }

  const belowThresholdSection =
    hiddenCount > 0 ? (
      <details
        className="group mt-2 pl-4"
        onClick={(e) => e.stopPropagation()}
        onToggle={(e) => {
          const isOpen = (e.target as HTMLDetailsElement).open
          handleBelowThresholdToggle(isOpen)
        }}
      >
        <summary
          className="text-xs text-coval-text-muted cursor-pointer hover:text-coval-text-muted list-none inline-flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-xs select-none transition-transform group-open:rotate-90">
            ▶
          </span>
          + {hiddenCount} below threshold
        </summary>
        <div className="space-y-1 mt-1">
          {hiddenEvents.map((sv, i) => renderRow(sv, i))}
        </div>
      </details>
    ) : null

  if (!groupByLabel) {
    if (filtered.length === 0) {
      return <div>{belowThresholdSection}</div>
    }
    const label = chipLabel ?? "Event"
    return (
      <div>
        <details className="group" onClick={(e) => e.stopPropagation()}>
          <summary
            className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer border-coval-border bg-surface-1 hover:bg-surface-1-hover list-none"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-xs select-none transition-transform group-open:rotate-90">
              ▶
            </span>
            <span className="text-sm font-medium text-coval-text-muted">
              {title}
            </span>
            <span className="text-sm text-coval-text-muted">
              {filtered.length}{" "}
              {filtered.length === 1
                ? label.toLowerCase()
                : `${label.toLowerCase()}s`}
            </span>
          </summary>
          <div className="space-y-1 mt-1 ml-6">
            {filtered.map((sv, i) => renderRow(sv, i))}
          </div>
        </details>
        {belowThresholdSection}
      </div>
    )
  }

  const grouped = new Map<string, SubvalueByTimestamp[]>()
  for (const sv of filtered) {
    const label = sv.string_value || chipLabel || "Event"
    const existing = grouped.get(label) ?? []
    existing.push(sv)
    grouped.set(label, existing)
  }

  const handleToggle = (label: string, isOpen: boolean) => {
    if (isOpen) {
      openLabelsRef.current.add(label)
    } else {
      openLabelsRef.current.delete(label)
    }
    if (!onFilterChange) {
      // Dispatch hover regions so opening a group scopes the waveform
      // highlight to just that group's subvalues (closing clears).
      if (openLabelsRef.current.size === 0) {
        simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_REGIONS_HOVER, {
          regions: null,
        })
        return
      }
      const visible: SubvalueByTimestamp[] = []
      for (const openLabel of openLabelsRef.current) {
        const events = grouped.get(openLabel)
        if (events) {
          visible.push(...events)
        }
      }
      const regions = visible.map((sv, idx) => {
        const severity = sv.float_value ?? 0
        const alpha = Math.min(0.25 + severity * 0.5, 0.75).toFixed(2)
        const color = regionColorFn
          ? regionColorFn(sv)
          : `rgba(59, 130, 246, ${alpha})`
        return {
          id: `anomaly_event_list_hover:${idx}`,
          start: sv.start_offset,
          end: Math.max(sv.end_offset, sv.start_offset + 0.5),
          color,
          drag: false,
          resize: false,
        }
      })
      simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_REGIONS_HOVER, {
        regions,
      })
      return
    }
    let aboveEvents: SubvalueByTimestamp[] | null = null
    if (openLabelsRef.current.size > 0) {
      const visible: SubvalueByTimestamp[] = []
      for (const openLabel of openLabelsRef.current) {
        const events = grouped.get(openLabel)
        if (events) {
          visible.push(...events)
        }
      }
      aboveEvents = visible
    }
    lastAboveRef.current = aboveEvents
    const belowEvents = belowThresholdOpenRef.current ? hiddenEvents : []
    const combined = [...(aboveEvents ?? []), ...belowEvents]
    onFilterChange(combined.length > 0 ? combined : null)
  }

  if (filtered.length === 0) {
    return <div>{belowThresholdSection}</div>
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground mb-3">
        {title} ({filtered.length} of {subvalues.length})
      </h3>
      <div className="space-y-2">
        {Array.from(grouped.entries()).map(([label, events]) => (
          <details
            key={label}
            className="group"
            onClick={(e) => e.stopPropagation()}
            onToggle={(e) => {
              const isOpen = (e.target as HTMLDetailsElement).open
              handleToggle(label, isOpen)
            }}
          >
            <summary
              className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer border-coval-border bg-surface-1 hover:bg-surface-1-hover list-none"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-xs select-none transition-transform group-open:rotate-90">
                ▶
              </span>
              {chipColorClassFn && events.length > 0 ? (
                <span
                  className={`px-2 py-0.5 rounded text-xs font-semibold border ${chipColorClassFn(events[0])}`}
                >
                  {label}
                </span>
              ) : (
                <span className="text-sm font-medium text-coval-text-muted">
                  {label}
                </span>
              )}
              <span className="text-sm text-coval-text-muted">
                {events.length} {events.length === 1 ? "event" : "events"}
              </span>
            </summary>
            <div className="space-y-1 mt-1 ml-6">
              {events.map((sv, i) => renderRow(sv, i))}
            </div>
          </details>
        ))}
      </div>
      {belowThresholdSection}
    </div>
  )
}

export { AnomalyEventList }
